/**
 * Sidebar.jsx
 *
 * Five sections:
 *   1. Sensor List      — all configured sensors with live health status
 *   2. Corridor Report  — period picker and PDF/CSV download, superuser only
 *   3. Connect your GIS — the OGC endpoint and layer names, superuser only
 *   4. Settings         — health threshold configuration
 *   5. About            — platform info
 *
 * Props:
 *   open        {bool}
 *   healthMap   {object}  device_id → health object
 *   healthError {bool}    true when /api/health failed — an empty healthMap
 *                         then means "unknown", not "still loading"
 *   apiBaseUrl  {string}
 *   appVersion  {string}
 *   isSuperuser {bool}    gates the Corridor Report section, matching
 *                         SUPERUSER_GET_PATHS on the API side, and the Connect
 *                         your GIS section, which is a UI consistency choice
 *                         rather than a boundary — see the comment there
 *   t           {function} translator
 *   language    {string}   current language tag — needed alongside t() because
 *                          formatLastSeen picks a plural form, which t() alone
 *                          cannot determine
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Divider, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatLastSeen, formatExactTime } from '../relativeTime';
import { apiFetch } from '../apiFetch';
import { downloadReport, periodError } from '../reportDownload';
import HelpTip from './HelpTip';

const STATUS_COLOR = {
  ok:      "#22c55e",
  warning: "#f59e0b",
  offline: "#6b7280",
  alert:   "#ef4444",
  unknown: "#9ca3af",
};

const STATUS_LABEL_KEY = {
  ok:      "statusOk",
  warning: "statusWarning",
  offline: "statusOffline",
  alert:   "statusAlert",
  unknown: "statusUnknown",
};

const statusLabel = (status, t) => t(STATUS_LABEL_KEY[status] ?? "statusUnknown");

// ── small reusable components ─────────────────────────────────────────────────

function StatusDot({ status }) {
  // Unrecognised status falls back to grey, never to green — an unknown state
  // must not be presented as healthy.
  return (
    <span style={{
      display: "inline-block",
      width: 10, height: 10,
      borderRadius: "50%",
      background: STATUS_COLOR[status] ?? STATUS_COLOR.unknown,
      marginRight: 8,
      flexShrink: 0,
    }} />
  );
}

function FieldRow({ label, children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
      <Typography variant="body2" sx={{ color: "text.secondary", mr: 2 }}>{label}</Typography>
      {children}
    </Box>
  );
}

function InlineNumberEdit({ value, min, max, step, unit, onSave, saving, disabled }) {
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState(String(value));
  const [error, setError] = useState(null);

  useEffect(() => { setInput(String(value)); }, [value]);
  // Leaving edit mode open while the config goes degraded would let the ✓
  // button write a fallback value back to the table.
  useEffect(() => { if (disabled) setEditing(false); }, [disabled]);

  const handleSave = async () => {
    const val = parseFloat(input);
    if (isNaN(val) || val < min || val > max) {
      setError(`Must be ${min}–${max}`);
      return;
    }
    setError(null);
    await onSave(val);
    setEditing(false);
  };

  if (disabled) {
    return (
      <span style={{ fontSize: 13, fontWeight: 600, color: "#9ca3af" }}>
        {value}{unit}
      </span>
    );
  }

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        style={{ padding: "2px 10px", border: "1px solid #d1d5db", borderRadius: 4, background: "#f9fafb", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#111827" }}
      >
        ✎ {value}{unit}
      </button>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <input
        type="number" value={input} min={min} max={max} step={step}
        onChange={e => { setInput(e.target.value); setError(null); }}
        autoFocus
        style={{ width: 60, padding: "2px 6px", border: `1px solid ${error ? "#ef4444" : "#3b82f6"}`, borderRadius: 4, fontSize: 13, textAlign: "center" }}
      />
      <span style={{ fontSize: 12, color: "#6b7280" }}>{unit}</span>
      <button
        onClick={handleSave} disabled={saving}
        style={{ padding: "2px 7px", border: "1px solid #10b981", borderRadius: 4, background: "#d1fae5", color: "#065f46", cursor: "pointer", fontSize: 13, fontWeight: 700 }}
      >
        {saving ? "…" : "✓"}
      </button>
      <button
        onClick={() => { setEditing(false); setInput(String(value)); setError(null); }}
        style={{ padding: "2px 7px", border: "1px solid #d1d5db", borderRadius: 4, background: "#f9fafb", color: "#6b7280", cursor: "pointer", fontSize: 13 }}
      >
        ✕
      </button>
      {error && <span style={{ fontSize: 11, color: "#ef4444" }}>{error}</span>}
    </Box>
  );
}

// ── corridor report ───────────────────────────────────────────────────────────
//
// The entry point for the three /report endpoints #131 shipped (#143). It lives
// in the sidebar rather than beside the drift chart because a corridor report
// describes the whole network over a period, not the asset whose pin happens to
// be selected.
//
// Not simulated below superuser, unlike every chart on the page: there is no
// fake corridor report to hand a signed-in non-superuser, and one invented for
// the purpose would be a document that reads as a statement about the real
// deployment. The section is therefore not rendered at all for them, and no
// request is issued — see the `isSuperuser` gate on the caller below.
//
// Closing the sidebar mid-assembly unmounts this without cancelling anything.
// That is deliberate: the file is delivered by chartExport's downloadBlob, which
// touches the document rather than this component's state, so a 30-second report
// still lands. Nothing here aborts a request the operator asked for because they
// collapsed the panel it was started from.

// How long "Done" stays on screen after the file has been handed over.
const REPORT_DONE_LINGER_MS = 1200;

function CorridorReport({ apiBaseUrl, t }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [committed, setCommitted] = useState({ from: "", to: "" });
  const [rangeError, setRangeError] = useState(null);
  // null = nothing to say, {pct, label} otherwise. Same shape as
  // TowerDriftChart's exportProgress.
  const [progress, setProgress] = useState(null);
  // Tracked separately from `progress` rather than derived from it, because the
  // "Done" label outlives the request that earned it. Deriving the disabled
  // state from `progress !== null` locked both buttons for the linger below —
  // which is exactly the PDF-then-CSV sequence the API's 10-minute cache exists
  // to make instant.
  const [inFlight, setInFlight] = useState(false);
  const [failed, setFailed] = useState(false);

  const doneTimer = useRef(null);
  useEffect(() => () => clearTimeout(doneTimer.current), []);

  const today = new Date().toISOString().split("T")[0];

  // Apply has not been pressed on a valid pair yet, so there is no period to ask
  // for. Same gate as TowerDriftChart's customPending and for the same reason: a
  // download fired before Apply would carry an empty range, which the API
  // answers with a 400 the operator would read as "the report failed".
  const pending = !(committed.from && committed.to);
  const disabled = inFlight || pending;

  const apply = () => {
    const key = periodError(from, to);
    if (key) { setRangeError(t(key)); return; }
    setRangeError(null);
    setFailed(false);
    setCommitted({ from, to });
  };

  const download = async (format) => {
    if (disabled) return;
    clearTimeout(doneTimer.current);
    setFailed(false);
    setInFlight(true);
    try {
      await downloadReport({
        apiBaseUrl,
        format,
        start: committed.from,
        end: committed.to,
        // The keys arrive from reportDownload.js and are translated here, so the
        // module stays free of the language context.
        onStage: (stage) => setProgress({ ...stage, label: t(stage.label) }),
      });
      // Held on "Done" briefly rather than snapping straight back to blank: the
      // second format of a period the API already has cached returns in
      // milliseconds, and without this the operator sees no acknowledgement at
      // all for a press that did produce a file.
      doneTimer.current = setTimeout(() => setProgress(null), REPORT_DONE_LINGER_MS);
    } catch (err) {
      // A period with no readings is not this branch — the endpoints answer it
      // with a document listing the devices that reported nothing. Everything
      // that reaches here is a failure to produce the report, and says so.
      console.error("Corridor report download failed:", err);
      setFailed(true);
      setProgress(null);
    } finally {
      setInFlight(false);
    }
  };

  return (
    <>
      <FieldRow label={t("customFrom")}>
        <input
          type="date" value={from} max={to || today}
          onChange={(e) => { setFrom(e.target.value); setRangeError(null); }}
          style={reportStyles.dateInput}
        />
      </FieldRow>
      <FieldRow label={t("customTo")}>
        <input
          type="date" value={to} min={from} max={today}
          onChange={(e) => { setTo(e.target.value); setRangeError(null); }}
          style={reportStyles.dateInput}
        />
      </FieldRow>

      <button onClick={apply} style={reportStyles.applyBtn}>{t("apply")}</button>

      {rangeError && (
        <Typography variant="caption" sx={{ color: "#ef4444", display: "block", mt: 1 }}>
          {rangeError}
        </Typography>
      )}

      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
        {pending
          ? t("reportPeriodPrompt")
          : t("reportPeriodSelected", { start: committed.from, end: committed.to })}
      </Typography>

      <Box sx={{ display: "flex", gap: 0.75, mt: 1 }}>
        <button
          onClick={() => download("pdf")} disabled={disabled}
          style={{ ...reportStyles.downloadBtn, opacity: disabled ? 0.5 : 1 }}
        >
          ⬇ {t("reportDownloadPdf")}
        </button>
        <button
          onClick={() => download("csv")} disabled={disabled}
          style={{ ...reportStyles.downloadBtn, opacity: disabled ? 0.5 : 1 }}
        >
          ⬇ {t("reportDownloadCsv")}
        </button>
      </Box>

      {/* A month-long report reads across eight DynamoDB tables, so a control
          that looked inert while it worked would be pressed again. The spinner
          is tied to the request rather than to the label, so "Done" is not
          still spinning. */}
      {progress && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          {inFlight && <CircularProgress size={14} />}
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {progress.label}
          </Typography>
        </Box>
      )}

      {failed && !inFlight && (
        <Typography variant="caption" sx={{ color: "#ef4444", display: "block", mt: 1 }}>
          {t("reportFailed")}
        </Typography>
      )}

      {/* Says up front that a sparse report is a real answer, so a document
          listing devices with no data is not read as a broken one. */}
      <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", mt: 1.5 }}>
        {t("reportEmptyPeriodNote")}
      </Typography>
    </>
  );
}

const reportStyles = {
  dateInput: { padding: "2px 6px", border: "1px solid #d1d5db", borderRadius: 4, fontSize: 12, color: "#374151", background: "#f9fafb" },
  applyBtn: { width: "100%", padding: "4px 12px", border: "1px solid #3b82f6", borderRadius: 6, background: "#eff6ff", color: "#1d4ed8", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  downloadBtn: { flex: 1, padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: 6, background: "#f9fafb", color: "#374151", cursor: "pointer", fontSize: 12, fontWeight: 600 },
};

// ── reporting watchdog ────────────────────────────────────────────────────────
//
// GET /api/watchdog (#155), rendered per device.
//
// The list shows EVERY device, not only the ones with findings, and that is the
// point rather than an oversight. #57, #103, #118, #135 and #149 are all the
// same lesson: a device with no recent data must be visibly distinguishable
// from one that is fine. An alert list containing two rows tells an operator
// nothing about the seven devices not in it — whether they were checked and
// passed, or were never checked at all.
//
// Four distinct colours for four distinct outcomes, and no shared "problem"
// red. "Stopped reporting" and "Never reported" have different remedies — a
// battery or a gateway versus finishing the provisioning — and a single colour
// for both would throw away the distinction this feature exists to draw.
//
// Not simulated below superuser, like the corridor report above and unlike
// every chart on the page: a fabricated watchdog verdict is a statement that
// the real deployment is healthy, which is the exact substitution #155 is
// about. The section is not rendered at all for those tiers and issues no
// request — see the `isSuperuser` gate on the caller.

const WATCHDOG_COLOR = {
  reporting: "#22c55e",
  reporting_not_measuring: "#f59e0b",
  silent: "#ef4444",
  never_reported: "#7c3aed",
  unknown: "#9ca3af",
};

const WATCHDOG_TINT = {
  reporting: "transparent",
  reporting_not_measuring: "#fffbeb",
  silent: "#fef2f2",
  never_reported: "#f5f3ff",
  unknown: "#f9fafb",
};

const WATCHDOG_LABEL_KEY = {
  reporting: "watchdogStateReporting",
  reporting_not_measuring: "watchdogStateNotMeasuring",
  silent: "watchdogStateSilent",
  never_reported: "watchdogStateNeverReported",
  unknown: "watchdogStateUnknown",
};

// Findings first, then by device id. An operator opening this panel during an
// incident should not have to scroll past the healthy devices to find the one
// that is not.
const WATCHDOG_ORDER = ["silent", "never_reported", "reporting_not_measuring", "unknown", "reporting"];

/**
 * A threshold or interval as "14 h 9 min".
 *
 * Deliberately abbreviated units rather than the translated, pluralised form
 * formatLastSeen produces, and the reason is worth stating so it is not
 * "corrected" later. This is a configuration quantity, not a moment in time,
 * and the identical string is what the watchdog's own alert email carries
 * (formatDuration in DataIngestionESM/lib/watchdog.mjs). An operator comparing
 * the email against this panel should be reading the same characters, and
 * `h`/`min`/`d`/`s` carry across both languages on this deployment.
 *
 * Elapsed time is NOT formatted here — that goes through formatLastSeen, which
 * is localised and plural-aware, and is the same helper the sensor list above
 * uses. The two questions are different and only one of them has a language.
 */
function formatSpan(seconds) {
  const total = Number(seconds);
  if (!Number.isFinite(total) || total < 0) return null;
  const s = Math.round(total);
  if (s < 60) return `${s} s`;
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const parts = [];
  if (days) parts.push(`${days} d`);
  if (hours) parts.push(`${hours} h`);
  if (minutes || !parts.length) parts.push(`${minutes} min`);
  return parts.join(" ");
}

function WatchdogDevice({ device, t, language }) {
  const colour = WATCHDOG_COLOR[device.state] ?? WATCHDOG_COLOR.unknown;
  const threshold = formatSpan(device.threshold_seconds);
  const ago = formatLastSeen(device.seconds_since_seen, t, language);

  let body = null;
  if (device.state === "never_reported") {
    body = t("watchdogNeverReportedBody");
  } else if (device.state === "unknown") {
    body = t("watchdogUnknownBody");
  } else if (device.state === "silent") {
    body = t("watchdogSilentBody", { ago: ago ?? "—", threshold: threshold ?? "—" });
  } else if (device.state === "reporting_not_measuring") {
    body = t("watchdogNotMeasuringBody", {
      empty: device.readings_without_measurement,
      sampled: device.readings_sampled,
    });
  } else {
    body = t("watchdogReportingBody", { ago: ago ?? "—", threshold: threshold ?? "—" });
  }

  // Shown on every device that has a threshold, including healthy ones. The
  // acceptance criterion is that the threshold is per device and derived from
  // measured cadence — an operator cannot check that without being told which
  // of the two sources decided this device's.
  const thresholdNote =
    device.threshold_source === "cadence"
      ? t("watchdogThresholdFromCadence")
      : device.threshold_source === "offline_threshold"
        ? t("watchdogThresholdFromFleet")
        : null;

  // The empty-payload count travels on every device, not only the one it
  // tripped the state on. A device drifting from 1% empty to 40% is not yet a
  // finding and is exactly what should be visible before it becomes one.
  const showEmptyNote =
    device.state !== "reporting_not_measuring" &&
    device.state !== "never_reported" &&
    device.readings_without_measurement > 0;

  return (
    <Box
      sx={{
        display: "flex", alignItems: "flex-start", gap: 1,
        mb: 1, p: 1, borderRadius: 1,
        bgcolor: WATCHDOG_TINT[device.state] ?? WATCHDOG_TINT.unknown,
        borderLeft: `3px solid ${colour}`,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          {device.label}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
          {device.device_id}
        </Typography>
        <Typography variant="caption" sx={{ color: colour, fontWeight: 700, display: "block", mt: 0.25 }}>
          {t(WATCHDOG_LABEL_KEY[device.state] ?? "watchdogStateUnknown")}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block" }}
          title={formatExactTime(device.last_seen) ?? undefined}
        >
          {body}
        </Typography>
        {thresholdNote && (
          <Typography variant="caption" sx={{ color: "#9ca3af", display: "block" }}>
            {thresholdNote}
          </Typography>
        )}
        {showEmptyNote && (
          <Typography variant="caption" sx={{ color: "#92400e", display: "block" }}>
            {t("watchdogEmptyNote", {
              empty: device.readings_without_measurement,
              sampled: device.readings_sampled,
            })}
          </Typography>
        )}
        {/* When this state began, from the stored state row. "Stopped
            reporting" without it cannot distinguish a device that missed one
            uplink from one that has been dead a fortnight. Absent until the
            watchdog sweep has run at least once. */}
        {device.previous?.since && device.state !== "reporting" && (
          <Typography variant="caption" sx={{ color: "#9ca3af", display: "block" }}>
            {t("watchdogSince", { time: formatExactTime(device.previous.since) ?? device.previous.since })}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function Watchdog({ apiBaseUrl, t, language }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    // Same res.ok guard as every other fetch on this page: an error body from
    // the API is still valid JSON, so without it a 503 would be rendered as a
    // successful response with no devices in it — which reads as a healthy
    // fleet, the one outcome this panel must never produce.
    apiFetch(`${apiBaseUrl}/api/watchdog`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP error: ${r.status}`); return r.json(); })
      .then((payload) => {
        if (cancelled) return;
        setData(payload);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Watchdog fetch error:", err);
        setFailed(true);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [apiBaseUrl]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={14} />
        <Typography variant="caption" sx={{ color: "text.secondary" }}>{t("watchdogLoading")}</Typography>
      </Box>
    );
  }

  if (failed || !data) {
    return (
      <Box>
        <Typography variant="body2" sx={{ color: "#991b1b", fontWeight: 600 }}>
          {t("watchdogError")}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
          {t("watchdogErrorHint")}
        </Typography>
      </Box>
    );
  }

  const devices = [...(data.devices ?? [])].sort((a, b) => {
    const rank = WATCHDOG_ORDER.indexOf(a.state) - WATCHDOG_ORDER.indexOf(b.state);
    return rank !== 0 ? rank : (a.device_id < b.device_id ? -1 : 1);
  });

  // The device list is the union of the sensor registry and the config table. If
  // the registry could not be read, a device that exists in ONLY the registry is
  // missing from this list entirely — and `em320-tilt-sn099`, the device this
  // whole feature was written to surface, is exactly such a device. A shorter
  // list with no explanation is the worst available outcome here, so the panel
  // says the list is incomplete rather than quietly presenting a partial fleet
  // as the whole one.
  const listIncomplete =
    data.registry_status === "unavailable" ||
    data.assets_status === "unavailable" ||
    data.config_status === "degraded";

  return (
    <>
      {/* The help sits beside the hint rather than beside the section heading:
          AccordionSummary is a ButtonBase, so a button nested in it would be
          invalid markup and a tap would also collapse the section. */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.25, mb: 1 }}>
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
          {t("watchdogHint")}
        </Typography>
        <HelpTip topic="watchdogCoverage" t={t} placement="bottom-end" />
      </Box>

      {listIncomplete && (
        <Box sx={{ mb: 1, p: 1, borderRadius: 1, bgcolor: "#fffbeb", border: "1px solid #fde68a" }}>
          <Typography variant="caption" sx={{ color: "#92400e", fontWeight: 700, display: "block" }}>
            {t("watchdogListIncomplete")}
          </Typography>
          <Typography variant="caption" sx={{ color: "#92400e", display: "block", mt: 0.25 }}>
            {t("watchdogListIncompleteHint")}
          </Typography>
        </Box>
      )}

      <Typography
        variant="body2"
        sx={{
          fontWeight: 700, mb: 1,
          color: data.devices_with_findings > 0 ? "#b91c1c" : "#15803d",
        }}
      >
        {data.devices_with_findings > 0
          ? t("watchdogFindings", { n: data.devices_with_findings, total: data.device_count })
          : t("watchdogAllReporting", { n: data.device_count })}
      </Typography>

      {devices.map((device) => (
        <WatchdogDevice key={device.device_id} device={device} t={t} language={language} />
      ))}

      {/* Permanent, and not a placeholder for work to come. #155 puts the
          declining-rate case explicitly out of scope, and a panel that did not
          say so would be read as covering it — which is how a device at 6% of
          its former rate goes unnoticed while a watchdog reports it green. */}
      <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", mt: 1.5 }}>
        {t("watchdogScopeNote")}
      </Typography>
    </>
  );
}

// ── connect your GIS ──────────────────────────────────────────────────────────
//
// The OGC endpoint and the two layer names, written down where a customer can
// find them (#164). #146 published the corridor over standards-compliant WMS and
// WFS, so a TSO's GIS department can point QGIS or ArcGIS at this URL and pull
// every segment and every date into the system they already run — but until this
// panel there was no URL anywhere in the interface, so the capability could only
// be claimed in a meeting, never checked by the customer on their own machine.
//
// IT FETCHES NOTHING. Every value below is a constant or derived from the base
// URL the map's WMS layer already uses. A label that can fail is a label that
// will eventually be wrong, and there is nothing here worth a request: the
// endpoint either answers when the customer's own GIS asks, or it does not, and
// probing it from the sidebar would only add a second way to be misinformed.
//
// Built from `apiBaseUrl` rather than hardcoded, so a copy taken from a local
// build says localhost instead of quietly handing out the production URL.

const OGC_ENDPOINT_PATH = "/geoserver/ows";
const OGC_LAYER_LATEST = "samst:segment_ndvi_latest";
const OGC_LAYER_SERIES = "samst:segment_ndvi_series";

// How long "✓ Copied" stays on the button. Same acknowledgement problem as the
// corridor report's "Done": without it a press that worked looks like a press
// that did nothing, and the URL gets copied twice.
const OGC_COPIED_LINGER_MS = 1600;

export function ogcEndpointUrl(apiBaseUrl) {
  const base = String(apiBaseUrl || "").replace(/\/+$/, "");
  return `${base || window.location.origin}${OGC_ENDPOINT_PATH}`;
}

// The endpoint above is a dispatcher: it routes to WMS or WFS on `service=`, so
// opened bare in a browser it answers `No service: ( OWS )`. That is correct OGC
// behaviour and every standards client depends on it — but it is also the first
// thing a person does with an address, and it makes a working endpoint look
// dead. Nobody who sees that exception goes on to open QGIS.
//
// So the panel offers the same endpoint a second time, with the parameters a
// client would have added. It renders as raw XML, which is ugly and
// unmistakably alive: it names both layers, their abstracts, and all ~1,515
// acquisition dates. That turns "trust me" into "look" without the customer
// installing anything.
//
// Do NOT put this URL in the copy field instead. `service=WMS` pins it to one
// service so it cannot also serve WFS, `version=` pins the protocol version
// clients are supposed to negotiate, and a client appends its own SERVICE and
// REQUEST anyway. Two controls, two jobs.
const OGC_CAPABILITIES_QUERY = "?service=WMS&version=1.3.0&request=GetCapabilities";

export function ogcCapabilitiesUrl(apiBaseUrl) {
  return `${ogcEndpointUrl(apiBaseUrl)}${OGC_CAPABILITIES_QUERY}`;
}

function OgcLayer({ name, description }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Box component="code" sx={ogcStyles.layerName}>{name}</Box>
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", lineHeight: 1.35 }}>
        {description}
      </Typography>
    </Box>
  );
}

function OgcEndpoints({ apiBaseUrl, t }) {
  const url = ogcEndpointUrl(apiBaseUrl);
  // null | "ok" | "manual" — "manual" is not an error state, it is the
  // instruction for finishing the copy by hand.
  const [copied, setCopied] = useState(null);
  const fieldRef = useRef(null);
  const copiedTimer = useRef(null);

  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const copy = async () => {
    clearTimeout(copiedTimer.current);
    // Selected before the write is attempted, so the fallback below is already
    // true by the time it is offered.
    fieldRef.current?.select?.();
    try {
      // Exactly the URL — no label, no trailing newline. It is going to be
      // pasted into a connection dialog that will not trim it.
      await navigator.clipboard.writeText(url);
      setCopied("ok");
      copiedTimer.current = setTimeout(() => setCopied(null), OGC_COPIED_LINGER_MS);
    } catch (err) {
      // `navigator.clipboard` is absent outside a secure context and the write
      // can be refused by permissions policy. The URL is already selected, so
      // say so rather than reporting a failure the reader cannot act on.
      setCopied("manual");
    }
  };

  return (
    <>
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1, lineHeight: 1.4 }}>
        {t("ogcIntro")}
      </Typography>

      <Typography variant="caption" sx={ogcStyles.label}>{t("ogcEndpointLabel")}</Typography>
      <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", mb: 0.5 }}>
        {/* readOnly rather than disabled: a disabled input cannot be selected,
            which would remove the only way to copy the URL when the clipboard
            API is unavailable. */}
        <input
          ref={fieldRef}
          readOnly
          value={url}
          aria-label={t("ogcEndpointLabel")}
          onFocus={(e) => e.target.select()}
          style={ogcStyles.urlField}
        />
        <button onClick={copy} style={ogcStyles.copyBtn}>
          {copied === "ok" ? `✓ ${t("ogcCopied")}` : t("ogcCopy")}
        </button>
      </Box>

      {copied === "manual" && (
        <Typography variant="caption" sx={{ color: "#b45309", display: "block", mb: 0.5 }}>
          {t("ogcCopyManual")}
        </Typography>
      )}

      <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", mb: 1, lineHeight: 1.35 }}>
        {t("ogcEndpointHint")}
      </Typography>

      {/* Opens in a new tab: this is a check performed alongside the panel, not
          a way out of it, and a customer part-way through setting up a
          connection should not lose the address they were copying. */}
      <a
        href={ogcCapabilitiesUrl(apiBaseUrl)}
        target="_blank"
        rel="noopener noreferrer"
        style={ogcStyles.verifyLink}
      >
        {t("ogcVerify")}
      </a>
      <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", mt: 0.25, mb: 1.5, lineHeight: 1.35 }}>
        {t("ogcVerifyHint")}
      </Typography>

      <Typography variant="caption" sx={ogcStyles.label}>{t("ogcLayersLabel")}</Typography>
      <OgcLayer name={OGC_LAYER_LATEST} description={t("ogcLayerLatest")} />
      <OgcLayer name={OGC_LAYER_SERIES} description={t("ogcLayerSeries")} />

      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5, lineHeight: 1.4 }}>
        {t("ogcClients")}
      </Typography>

      {/* The caveat travels with the data, because nothing else will. Once these
          values are a layer in someone else's GIS, every bit of context that is
          not attached to the number is gone — the layer abstracts carry it into
          QGIS, and this panel carries it to whoever sets the connection up.

          Two strings, and the first of them is `helpNdviClaim` — the same key
          the corridor segment layer renders and the same key the help tips
          render. Until #171 this panel held its own second copy of that claim,
          `ogcCaveat`, kept in step with the layer's copy by a comment. Only the
          second sentence here was ever specific to an export, so only it is
          still a string of its own. */}
      <Typography variant="caption" sx={{ color: "#6b7280", display: "block", mt: 1.5, lineHeight: 1.4 }}>
        {t("helpNdviClaim")} {t("ogcCaveatGis")}
      </Typography>
    </>
  );
}

const ogcStyles = {
  label: { color: "#6b7280", fontWeight: 700, display: "block", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.04em", fontSize: 10 },
  urlField: { flex: 1, minWidth: 0, padding: "4px 6px", border: "1px solid #d1d5db", borderRadius: 4, background: "#f9fafb", color: "#111827", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 11 },
  copyBtn: { flexShrink: 0, padding: "4px 10px", border: "1px solid #3b82f6", borderRadius: 6, background: "#eff6ff", color: "#1d4ed8", cursor: "pointer", fontSize: 12, fontWeight: 600 },
  layerName: { display: "block", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 11, color: "#111827", wordBreak: "break-all" },
  verifyLink: { color: "#1d4ed8", fontWeight: 600, fontSize: 11.5, textDecoration: "underline" },
};

// ── main component ────────────────────────────────────────────────────────────

export default function Sidebar({ open, healthMap = {}, healthError = false, apiBaseUrl, appVersion = "1.0.0", isSuperuser = false, t = (k) => k, language = 'en' }) {
  const [healthConfig, setHealthConfig] = useState({ offline_after_hours: 2, battery_warning_pct: 20 });
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(false);
  const [configDegraded, setConfigDegraded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setConfigLoading(true);
    setConfigError(false);
    apiFetch(`${apiBaseUrl}/api/config/health`)
      .then(r => { if (!r.ok) throw new Error(`HTTP error: ${r.status}`); return r.json(); })
      .then(data => {
        if (cancelled) return;
        // Keep only the two editable fields — config_status is a transport
        // signal about these values, not one of them, and spreading it into
        // state would echo it back in the PUT body.
        setHealthConfig({
          offline_after_hours: data.offline_after_hours,
          battery_warning_pct: data.battery_warning_pct,
        });
        setConfigDegraded(data.config_status === "degraded");
        setConfigLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        console.error("Health config fetch error:", err);
        setConfigError(true);
        setConfigLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, apiBaseUrl]);

  // When the displayed values are fallbacks rather than the saved settings,
  // editing one field would PUT the other field's fallback back over the real
  // stored value. Lock the form until the config service answers properly.
  const configLocked = configDegraded || configError;

  const saveHealthConfig = async (field, val) => {
    if (configLocked) return;
    setSaving(true);
    setSaveError(null);
    const updated = { ...healthConfig, [field]: val };
    try {
      const res = await apiFetch(`${apiBaseUrl}/api/config/health`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          offline_after_hours: updated.offline_after_hours,
          battery_warning_pct: updated.battery_warning_pct,
        }),
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      setHealthConfig(updated);
    } catch (err) {
      setSaveError("Failed to save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const sensors = Object.values(healthMap);

  if (!open) return null;

  return (
    <Box sx={{ width: "100%", px: 2, pt: 1, pb: 4, overflowY: "auto", height: "100%" }}>

      {/* ── Sensor List ── */}
      <Accordion defaultExpanded disableGutters elevation={0} sx={{ bgcolor: "transparent", "&:before": { display: "none" } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", fontSize: 11, color: "#6b7280" }}>
{t("sensorList")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          {healthError ? (
            // An empty list during an outage used to render as "Loading…",
            // telling the operator to wait instead of that something is broken.
            <Box>
              <Typography variant="body2" sx={{ color: "#991b1b", fontWeight: 600 }}>
                {t("sensorHealthError")}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                {t("healthRetrying")}
              </Typography>
            </Box>
          ) : sensors.length === 0 ? (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>{t("loading")}</Typography>
          ) : (
            <>
            {/* One help control for the whole column of dots, at its head,
                rather than one per row. It cannot go beside "Sensor List"
                above: AccordionSummary is a ButtonBase, so a button inside it
                would be invalid markup and a tap would collapse the section. */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
              <HelpTip topic="deviceStatus" t={t} placement="bottom-end" />
            </Box>
            {sensors.map(s => (
              <Box key={s.device_id} sx={{ display: "flex", alignItems: "flex-start", mb: 1.5, pb: 1.5, borderBottom: "1px solid #f3f4f6" }}>
                <StatusDot status={s.status} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{s.label}</Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>{s.device_id}</Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: STATUS_COLOR[s.status] ?? STATUS_COLOR.unknown }}
                    title={formatExactTime(s.last_seen) ?? undefined}
                  >
                    {statusLabel(s.status, t)}
                    {s.battery != null ? ` · 🔋 ${s.battery}%` : ""}
                    {s.seconds_since_seen != null
                      ? ` · ${formatLastSeen(s.seconds_since_seen, t, language)}`
                      : ""}
                  </Typography>
                </Box>
              </Box>
            ))}
            </>
          )}
        </AccordionDetails>
      </Accordion>

      <Divider sx={{ my: 1 }} />

      {/* ── Reporting watchdog ── Superuser only, and hidden rather than
          simulated below that tier: every other figure on the page is fake
          data for those accounts, and a real watchdog verdict sitting among
          fake sensors is unreadable — while a fake one would be a statement
          that the real deployment is healthy. Nothing here fetches unless the
          section renders, so no request is issued for those tiers.

          Placed directly under the sensor list, which shows the same devices
          with their live health. That list answers "what is this device doing";
          this one answers "is it still talking to us at all", and until #155
          nothing on this dashboard answered the second question. */}
      {isSuperuser && (
        <>
          <Accordion defaultExpanded disableGutters elevation={0} sx={{ bgcolor: "transparent", "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", fontSize: 11, color: "#6b7280" }}>
                {t("watchdog")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 0 }}>
              <Watchdog apiBaseUrl={apiBaseUrl} t={t} language={language} />
            </AccordionDetails>
          </Accordion>

          <Divider sx={{ my: 1 }} />
        </>
      )}

      {/* ── Corridor Report ── Superuser only. Hidden rather than disabled: the
          three /report paths are in SUPERUSER_GET_PATHS, so for every other
          tier this control could only ever produce a 401, and a disabled
          button advertises a feature the account cannot have. Nothing inside
          fetches on mount, so an operator below superuser issues no request. */}
      {isSuperuser && (
        <>
          {/* Expanded by default, like the other two working sections and
              unlike About: the defect #143 reports is that the report has no
              entry point, and one behind a collapsed heading is barely a
              better answer than none. */}
          <Accordion defaultExpanded disableGutters elevation={0} sx={{ bgcolor: "transparent", "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", fontSize: 11, color: "#6b7280" }}>
                {t("corridorReport")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 0 }}>
              <CorridorReport apiBaseUrl={apiBaseUrl} t={t} />
            </AccordionDetails>
          </Accordion>

          <Divider sx={{ my: 1 }} />
        </>
      )}

      {/* ── Connect your GIS ── Superuser only, and that gate is a UI decision,
          NOT a security boundary. Nobody should later mistake it for one: the
          OGC endpoints behind it are unauthenticated by necessity, because a
          Leaflet tile request is a plain browser GET that cannot carry an
          Authorization header (#138), so nginx leaves /geoserver/{ows,wms,wfs}
          open and the corridor NDVI is already readable by anyone who knows the
          URL. Hiding this panel from signed-out visitors keeps it consistent
          with every other /api/ consumer in the dashboard; it protects nothing.
          The corollary, recorded in DEPLOY.md §2.11g: nothing may be published
          to that workspace that is not fit to be public.

          Expanded by default for the same reason as the corridor report above —
          the defect #164 reports is that the capability is undiscoverable, and
          one behind a collapsed heading is barely a better answer than none. */}
      {isSuperuser && (
        <>
          <Accordion defaultExpanded disableGutters elevation={0} sx={{ bgcolor: "transparent", "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", fontSize: 11, color: "#6b7280" }}>
                {t("ogcTitle")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 0 }}>
              <OgcEndpoints apiBaseUrl={apiBaseUrl} t={t} />
            </AccordionDetails>
          </Accordion>

          <Divider sx={{ my: 1 }} />
        </>
      )}

      {/* ── Settings ── */}
      <Accordion defaultExpanded disableGutters elevation={0} sx={{ bgcolor: "transparent", "&:before": { display: "none" } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", fontSize: 11, color: "#6b7280" }}>
{t("settings")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          {configLoading ? (
            <CircularProgress size={16} />
          ) : (
            <>
              {configLocked && (
                <Box sx={{ mb: 1.5, p: 1, borderRadius: 1, bgcolor: "#fffbeb", border: "1px solid #fde68a" }}>
                  <Typography variant="caption" sx={{ color: "#92400e", fontWeight: 700, display: "block" }}>
                    {configError ? t("configLoadError") : t("configDegradedBadge")}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#92400e", display: "block", mt: 0.25 }}>
                    {t("configDegradedNotice")}
                  </Typography>
                </Box>
              )}
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>
  {t("healthThresholds")}
              </Typography>
    <FieldRow label={t("offlineAfter")}>
                <InlineNumberEdit
                  value={healthConfig.offline_after_hours}
                  min={0.5} max={48} step={0.5} unit="h"
                  saving={saving}
                  disabled={configLocked}
                  onSave={(val) => saveHealthConfig("offline_after_hours", val)}
                />
              </FieldRow>
    <FieldRow label={t("batteryWarningBelow")}>
                <InlineNumberEdit
                  value={healthConfig.battery_warning_pct}
                  min={5} max={50} step={1} unit="%"
                  saving={saving}
                  disabled={configLocked}
                  onSave={(val) => saveHealthConfig("battery_warning_pct", val)}
                />
              </FieldRow>
              {saveError && (
                <Typography variant="caption" sx={{ color: "#ef4444" }}>{t("failedToSave")}</Typography>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>

      <Divider sx={{ my: 1 }} />

      {/* ── About ── */}
      <Accordion disableGutters elevation={0} sx={{ bgcolor: "transparent", "&:before": { display: "none" } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", fontSize: 11, color: "#6b7280" }}>
{t("about")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>SAMST Platform</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>Version {appVersion}</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
            {t("aboutDescription")}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
            {t("poweredBy")}
          </Typography>
          <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", mt: 1.5 }}>
            © {new Date().getFullYear()} LoRaSi
          </Typography>
        </AccordionDetails>
      </Accordion>

    </Box>
  );
}
